"use client";

import React, { useState, useRef, useEffect } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation"; 
import { useLocale } from "@/context/LocaleContext";
import { useSidebar } from "@/context/SidebarContext";
import { locales } from "@/language";

type LocaleType = (typeof locales)[number];

interface LanguageToggleButtonProps {
    fullWidth?: boolean;
    direction?: "up" | "down";
    showLabel?: boolean;
}

const LanguageToggleButton: React.FC<LanguageToggleButtonProps> = ({
    fullWidth = false,
    direction = "up",
    showLabel = true,
}) => {
    const [open, setOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    const { locale } = useLocale();
    const { isMobileOpen } = useSidebar();
    
    const pathname = usePathname() || "";
    const router = useRouter();
    const searchParams = useSearchParams(); 

    const toggle = () => setOpen(!open);

    const handleSelect = (lang: LocaleType) => {
        setOpen(false);

        if (lang === locale) return;

        
        if (pathname.startsWith("/s/") || pathname === "/s") {
            const currentParams = new URLSearchParams(searchParams.toString());
            currentParams.set("lang", lang);

            router.push(`${pathname}?${currentParams.toString()}`);
            return; 
        }

        let pathWithoutLocale = pathname;
        if (pathname.startsWith("/en/") || pathname === "/en") {
            pathWithoutLocale = pathname.replace(/^\/en/, "") || "/";
        }

        const currentQuery = searchParams.toString();
        const queryString = currentQuery ? `?${currentQuery}` : "";

        if (lang === "en") {
            const newPath = pathWithoutLocale === "/" ? "/en" : `/en${pathWithoutLocale}`;
            router.push(`${newPath}${queryString}`);
        } else {
            router.push(`${pathWithoutLocale}${queryString}`);
        }
    };

    useEffect(() => { setOpen(false); }, [pathname]);
    useEffect(() => { if (!isMobileOpen) setOpen(false); }, [isMobileOpen]);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    return (
        <div className={`relative ${fullWidth ? "w-full" : "w-auto"}`} ref={containerRef}>
            <button
                onClick={toggle}
                className={`flex items-center px-3 py-2 
                    hover:bg-gray-100 dark:hover:bg-gray-800
                    active:bg-gray-100 dark:active:bg-gray-800
                    text-gray-700 dark:text-gray-300 transition-colors group
                    ${fullWidth ? "w-full justify-between" : "gap-2"} 
                `}
            >
                <div className="flex items-center gap-2">
                    {locale === "id" ? (
                        <svg width="24" height="24" viewBox="0 0 3 2" className="flex-shrink-0 rounded-[2px] shadow-sm w-6 h-4 object-cover">
                            <rect width="3" height="1" fill="#CE1126" />
                            <rect y="1" width="3" height="1" fill="#FFFFFF" />
                        </svg>
                    ) : (
                        <svg width="24" height="24" viewBox="0 0 60 30" className="flex-shrink-0 rounded-[2px] shadow-sm w-6 h-4 object-cover">
                            <clipPath id="s"><path d="M0,0 v30 h60 v-30 z" /></clipPath>
                            <clipPath id="t"><path d="M30,15 h30 v15 z v-30 z h-30 z h-30 z v30 z h30 z" /></clipPath>
                            <g clipPath="url(#s)">
                                <path d="M0,0 v30 h60 v-30 z" fill="#012169" />
                                <path d="M0,0 L60,30 M60,0 L0,30" stroke="#fff" strokeWidth="6" />
                                <path d="M0,0 L60,30 M60,0 L0,30" clipPath="url(#t)" stroke="#C8102E" strokeWidth="4" />
                                <path d="M30,0 v30 M0,15 h60" stroke="#fff" strokeWidth="10" />
                                <path d="M30,0 v30 M0,15 h60" stroke="#C8102E" strokeWidth="6" />
                            </g>
                        </svg>
                    )}
                    {showLabel && (
                        <span className={`text-sm font-medium ${fullWidth ? "block" : "hidden md:block"}`}>
                            {locale === "id" ? "Indonesia" : "English"}
                        </span>
                    )}
                </div>
                <svg
                    className={`w-4 h-4 text-gray-500 transition-transform duration-200 ml-auto ${!open ? "rotate-180" : ""}`}
                    fill="none" stroke="currentColor" viewBox="0 0 24 24"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
            </button>

            {open && (
                <div
                    className={`absolute bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-1 z-50 animate-in fade-in zoom-in-95 duration-100
                        ${fullWidth ? "w-full left-0" : "w-40 right-0"} 
                        ${direction === "up" ? "bottom-full mb-2" : "top-full mt-2"}
                    `}
                >
                    {locales.map((lang) => (
                        <button
                            key={lang}
                            onClick={() => handleSelect(lang)}
                            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-left
                                transition-colors whitespace-nowrap
                                hover:bg-gray-100 dark:hover:bg-gray-800
                                ${
                                    locale === lang
                                        ? "bg-gray-50 dark:bg-gray-800/50 font-medium text-rose-600 dark:text-rose-400"
                                        : "text-gray-700 dark:text-gray-300"
                                }
                            `}
                        >
                            {lang === "id" ? (
                                <svg width="20" height="20" viewBox="0 0 3 2" className="shadow-sm w-5 h-auto">
                                    <rect width="3" height="1" fill="#CE1126" />
                                    <rect y="1" width="3" height="1" fill="#FFFFFF" />
                                </svg>
                            ) : (
                                <svg width="20" height="20" viewBox="0 0 60 30" className="shadow-sm w-5 h-auto">
                                    <clipPath id="s"><path d="M0,0 v30 h60 v-30 z" /></clipPath>
                                    <clipPath id="t"><path d="M30,15 h30 v15 z v-30 z h-30 z h-30 z v30 z h30 z" /></clipPath>
                                    <g clipPath="url(#s)">
                                        <path d="M0,0 v30 h60 v-30 z" fill="#012169" />
                                        <path d="M0,0 L60,30 M60,0 L0,30" stroke="#fff" strokeWidth="6" />
                                        <path d="M0,0 L60,30 M60,0 L0,30" clipPath="url(#t)" stroke="#C8102E" strokeWidth="4" />
                                        <path d="M30,0 v30 M0,15 h60" stroke="#fff" strokeWidth="10" />
                                        <path d="M30,0 v30 M0,15 h60" stroke="#C8102E" strokeWidth="6" />
                                    </g>
                                </svg>
                            )}
                            <span className="text-sm">{lang === "id" ? "Indonesia" : "English"}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

export default LanguageToggleButton;